import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { X, Download, FileText, Star, TrendingUp, Clock, MapPin, DollarSign, Briefcase } from 'lucide-react';

const API = 'https://talentflow-backend-production-09dd.up.railway.app/api';

const COLUMNS = [
  { id: 'pending', label: 'Applied', color: '#6366f1', bg: '#ede9fe' },
  { id: 'ai_interview_pending', label: 'AI Interview', color: '#f59e0b', bg: '#fef9c3' },
  { id: 'ai_interview_done', label: 'Interview Done', color: '#0ea5e9', bg: '#e0f2fe' },
  { id: 'shortlisted', label: 'Shortlisted', color: '#16a34a', bg: '#dcfce7' },
  { id: 'rejected', label: 'Rejected', color: '#dc2626', bg: '#fee2e2' },
  { id: 'hired', label: 'Hired', color: '#7c3aed', bg: '#f5f3ff' },
];

const scoreColor = (s) => !s ? '#94a3b8' : s >= 8 ? '#16a34a' : s >= 6 ? '#f59e0b' : '#dc2626';
const scoreLabel = (s) => !s ? 'N/A' : s >= 8 ? 'Excellent' : s >= 6 ? 'Good' : 'Needs Work';

function CandidatePanel({ candidate, token, onClose, onStatusChange }) {
  const [downloading, setDownloading] = useState('');

  const downloadFile = async (type) => {
    setDownloading(type);
    try {
      const r = await fetch(`${API}/candidates/${type}/${candidate._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!r.ok) { alert(`${type === 'resume' ? 'Resume' : 'Report'} not available`); return; }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type === 'resume' ? 'Resume' : 'AI_Report'}_${candidate.name.replace(/\s/g,'_')}.pdf`;
      a.click();
    } catch { alert('Download failed!'); }
    setDownloading('');
  };

  return (
    <>
      {/* Overlay */}
      <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:999 }}/>
      
      {/* Panel */}
      <div style={{ position:'fixed', right:0, top:0, bottom:0, width:'420px', background:'white', zIndex:1000, boxShadow:'-8px 0 40px rgba(0,0,0,0.15)', overflowY:'auto', fontFamily:'-apple-system,BlinkMacSystemFont,"Inter",sans-serif' }}>
        
        {/* Header */}
        <div style={{ background:'#0f172a', padding:'24px', position:'sticky', top:0, zIndex:10 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
            <div>
              <div style={{ width:'44px', height:'44px', borderRadius:'50%', background:'linear-gradient(135deg,#6366f1,#8b5cf6)', display:'flex', alignItems:'center', justifyContent:'center', color:'white', fontWeight:'700', fontSize:'18px', marginBottom:'12px' }}>
                {candidate.name?.charAt(0).toUpperCase()}
              </div>
              <h2 style={{ color:'white', fontSize:'18px', fontWeight:'700', margin:'0 0 4px' }}>{candidate.name}</h2>
              <p style={{ color:'rgba(255,255,255,0.5)', fontSize:'12px', margin:0 }}>{candidate.requirement?.title || 'N/A'}</p>
            </div>
            <button onClick={onClose} style={{ background:'rgba(255,255,255,0.1)', border:'none', borderRadius:'8px', width:'32px', height:'32px', cursor:'pointer', color:'white', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <X size={16}/>
            </button>
          </div>

          {/* Status Badge */}
          <div style={{ marginTop:'16px', display:'flex', alignItems:'center', gap:'8px' }}>
            {COLUMNS.map(col => (
              <button key={col.id} onClick={() => onStatusChange(candidate._id, col.id)}
                style={{ padding:'4px 10px', borderRadius:'20px', border:'none', cursor:'pointer', fontSize:'10px', fontWeight:'700', fontFamily:'inherit',
                  background: candidate.status === col.id ? col.color : 'rgba(255,255,255,0.1)',
                  color: candidate.status === col.id ? 'white' : 'rgba(255,255,255,0.4)',
                  transform: candidate.status === col.id ? 'scale(1.05)' : 'scale(1)',
                  transition:'all 0.15s' }}>
                {col.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ padding:'20px' }}>

          {/* AI Score */}
          {candidate.aiScore && (
            <div style={{ background:'linear-gradient(135deg,#0f172a,#1e1b4b)', borderRadius:'12px', padding:'16px', marginBottom:'16px', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div>
                <div style={{ color:'rgba(255,255,255,0.5)', fontSize:'11px', marginBottom:'4px' }}>FINAL AI SCORE</div>
                <div style={{ color:scoreColor(candidate.aiScore), fontSize:'28px', fontWeight:'800' }}>{candidate.aiScore}<span style={{ fontSize:'14px', color:'rgba(255,255,255,0.4)' }}>/10</span></div>
                <div style={{ color:scoreColor(candidate.aiScore), fontSize:'11px', fontWeight:'600' }}>{scoreLabel(candidate.aiScore)}</div>
              </div>
              {candidate.videoScore && (
                <div style={{ textAlign:'right' }}>
                  <div style={{ color:'rgba(255,255,255,0.4)', fontSize:'10px', marginBottom:'4px' }}>VIDEO SCORE</div>
                  <div style={{ color:'#a5b4fc', fontSize:'22px', fontWeight:'700' }}>{candidate.videoScore}/10</div>
                </div>
              )}
            </div>
          )}

          {/* Quick Info */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'16px' }}>
            {[
              { icon: Briefcase, label:'Experience', value: candidate.experience + ' years' },
              { icon: DollarSign, label:'Expected Rate', value: 'Rs.' + (candidate.expectedRate?.toLocaleString() || 'N/A') + '/mo' },
              { icon: Clock, label:'Availability', value: candidate.availability || 'N/A' },
              { icon: Clock, label:'Notice Period', value: candidate.noticePeriod || 'N/A' },
            ].map(({ icon: Icon, label, value }) => (
              <div key={label} style={{ background:'#f9fafb', borderRadius:'8px', padding:'12px' }}>
                <div style={{ color:'#9ca3af', fontSize:'10px', fontWeight:'600', marginBottom:'4px', display:'flex', alignItems:'center', gap:'4px' }}>
                  <Icon size={10}/> {label}
                </div>
                <div style={{ color:'#0f172a', fontSize:'13px', fontWeight:'600' }}>{value}</div>
              </div>
            ))}
          </div>

          {/* Skills */}
          {candidate.skills?.length > 0 && (
            <div style={{ marginBottom:'16px' }}>
              <div style={{ color:'#374151', fontSize:'12px', fontWeight:'700', marginBottom:'8px' }}>SKILLS</div>
              <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
                {candidate.skills.map(s => (
                  <span key={s} style={{ padding:'4px 10px', background:'#ede9fe', color:'#6366f1', borderRadius:'20px', fontSize:'11px', fontWeight:'600' }}>{s}</span>
                ))}
              </div>
            </div>
          )}

          {/* AI Summary */}
          {candidate.interviewSummary && (
            <div style={{ background:'#f0f9ff', borderRadius:'10px', padding:'14px', marginBottom:'16px', borderLeft:'3px solid #6366f1' }}>
              <div style={{ color:'#374151', fontSize:'11px', fontWeight:'700', marginBottom:'6px' }}>AI ASSESSMENT</div>
              <p style={{ color:'#475569', fontSize:'12px', margin:0, lineHeight:'1.6' }}>{candidate.interviewSummary}</p>
            </div>
          )}

          {/* Strengths */}
          {candidate.interviewStrengths?.length > 0 && (
            <div style={{ marginBottom:'12px' }}>
              <div style={{ color:'#16a34a', fontSize:'11px', fontWeight:'700', marginBottom:'6px' }}>STRENGTHS</div>
              {candidate.interviewStrengths.map((s, i) => (
                <div key={i} style={{ display:'flex', gap:'6px', alignItems:'flex-start', marginBottom:'4px' }}>
                  <span style={{ color:'#16a34a', fontSize:'12px' }}>+</span>
                  <span style={{ color:'#374151', fontSize:'12px' }}>{s}</span>
                </div>
              ))}
            </div>
          )}

          {/* Improvements */}
          {candidate.interviewImprovements?.length > 0 && (
            <div style={{ marginBottom:'16px' }}>
              <div style={{ color:'#f59e0b', fontSize:'11px', fontWeight:'700', marginBottom:'6px' }}>AREAS TO IMPROVE</div>
              {candidate.interviewImprovements.map((s, i) => (
                <div key={i} style={{ display:'flex', gap:'6px', alignItems:'flex-start', marginBottom:'4px' }}>
                  <span style={{ color:'#f59e0b', fontSize:'12px' }}>-</span>
                  <span style={{ color:'#374151', fontSize:'12px' }}>{s}</span>
                </div>
              ))}
            </div>
          )}

          {/* Download Buttons */}
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginTop:'8px' }}>
            <button onClick={() => downloadFile('resume')} disabled={downloading === 'resume'}
              style={{ padding:'12px', background:'#0f172a', color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontSize:'13px', fontWeight:'600', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:'6px' }}>
              <Download size={14}/> {downloading === 'resume' ? 'Loading...' : 'Resume'}
            </button>
            {candidate.aiScore && (
              <button onClick={() => downloadFile('report')} disabled={downloading === 'report'}
                style={{ padding:'12px', background:'#6366f1', color:'white', border:'none', borderRadius:'8px', cursor:'pointer', fontSize:'13px', fontWeight:'600', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:'6px' }}>
                <FileText size={14}/> {downloading === 'report' ? 'Loading...' : 'AI Report'}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default function KanbanView({ token, requirements, user }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  useEffect(() => { loadCandidates(); }, []);

  const loadCandidates = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/candidates`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (d.success) setCandidates(d.candidates || []);
    } catch(e) { console.log('Kanban error:', e); }
    setLoading(false);
  };

  const onDragEnd = async (result) => {
    if (!result.destination) return;
    if (user?.role === 'vendor') return;
    const { draggableId, destination } = result;
    const newStatus = destination.droppableId;
    setCandidates(prev => prev.map(c => c._id === draggableId ? { ...c, status: newStatus } : c));
    try {
      await fetch(`${API}/candidates/${draggableId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus })
      });
    } catch {}
  };

  const handleStatusChange = async (id, newStatus) => {
    setCandidates(prev => prev.map(c => c._id === id ? { ...c, status: newStatus } : c));
    if (selected?._id === id) setSelected(prev => ({ ...prev, status: newStatus }));
    try {
      await fetch(`${API}/candidates/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ status: newStatus })
      });
    } catch {}
  };

  const getCandidatesByStatus = (status) => candidates.filter(c => c.status === status);

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Inter", sans-serif' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px' }}>
        <div>
          <h2 style={{ color:'#0f172a', fontSize:'16px', fontWeight:'700', margin:'0 0 4px' }}>Hiring Pipeline</h2>
          <p style={{ color:'#6b7280', fontSize:'13px', margin:0 }}>{candidates.length} candidates total</p>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign:'center', padding:'60px', color:'#9ca3af' }}>Loading pipeline...</div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(6, minmax(200px, 1fr))', gap:'12px', overflowX:'auto', paddingBottom:'8px' }}>
            {COLUMNS.map(col => (
              <div key={col.id} style={{ minWidth:'180px' }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'10px', padding:'8px 12px', background:col.bg, borderRadius:'8px' }}>
                  <span style={{ color:col.color, fontSize:'12px', fontWeight:'700' }}>{col.label}</span>
                  <span style={{ background:col.color, color:'white', borderRadius:'50%', width:'20px', height:'20px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'11px', fontWeight:'700' }}>
                    {getCandidatesByStatus(col.id).length}
                  </span>
                </div>

                <Droppable droppableId={col.id}>
                  {(provided, snapshot) => (
                    <div ref={provided.innerRef} {...provided.droppableProps}
                      style={{ minHeight:'400px', background: snapshot.isDraggingOver ? '#f0f9ff' : '#f9fafb', borderRadius:'10px', padding:'8px', border:`2px dashed ${snapshot.isDraggingOver ? col.color : '#e5e7eb'}`, transition:'all 0.2s' }}>
                      
                      {getCandidatesByStatus(col.id).map((c, index) => (
                        <Draggable key={c._id} draggableId={c._id} index={index}>
                          {(provided, snapshot) => (
                            <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
                              onClick={() => setSelected(c)}
                              style={{ background:'white', borderRadius:'8px', padding:'12px', marginBottom:'8px', border:'1px solid #e5e7eb', boxShadow: snapshot.isDragging ? '0 8px 24px rgba(0,0,0,0.12)' : '0 1px 3px rgba(0,0,0,0.04)', cursor:'pointer', transition:'all 0.15s', ...provided.draggableProps.style }}
                              onMouseEnter={e => { e.currentTarget.style.borderColor='#6366f1'; e.currentTarget.style.boxShadow='0 2px 8px rgba(99,102,241,0.15)'; }}
                              onMouseLeave={e => { e.currentTarget.style.borderColor='#e5e7eb'; e.currentTarget.style.boxShadow='0 1px 3px rgba(0,0,0,0.04)'; }}>
                              
                              <div style={{ color:'#0f172a', fontSize:'13px', fontWeight:'600', marginBottom:'4px' }}>{c.name}</div>
                              {c.requirement?.title && (
                                <div style={{ color:'#6b7280', fontSize:'10px', marginBottom:'6px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.requirement.title}</div>
                              )}
                              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'6px' }}>
                                <span style={{ color:'#6b7280', fontSize:'11px' }}>{c.experience} yrs</span>
                                {c.aiScore && (
                                  <span style={{ background: scoreColor(c.aiScore) + '15', color:scoreColor(c.aiScore), fontSize:'10px', fontWeight:'700', padding:'2px 6px', borderRadius:'4px' }}>
                                    AI: {c.aiScore}/10
                                  </span>
                                )}
                              </div>
                              {c.skills?.length > 0 && (
                                <div style={{ display:'flex', gap:'3px', flexWrap:'wrap' }}>
                                  {c.skills.slice(0,2).map(s => (
                                    <span key={s} style={{ padding:'1px 6px', background:'#f3f4f6', color:'#374151', borderRadius:'3px', fontSize:'9px', fontWeight:'500' }}>{s}</span>
                                  ))}
                                  {c.skills.length > 2 && <span style={{ color:'#9ca3af', fontSize:'9px' }}>+{c.skills.length - 2}</span>}
                                </div>
                              )}
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                      {getCandidatesByStatus(col.id).length === 0 && (
                        <div style={{ textAlign:'center', padding:'20px', color:'#d1d5db', fontSize:'12px' }}>Drop here</div>
                      )}
                    </div>
                  )}
                </Droppable>
              </div>
            ))}
          </div>
        </DragDropContext>
      )}

      {selected && (
        <CandidatePanel
          candidate={selected}
          token={token}
          onClose={() => setSelected(null)}
          onStatusChange={handleStatusChange}
        />
      )}
    </div>
  );
}
